import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { MarkEmailRead as MarkEmailReadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { apiClient } from '../../services/api';

export function VerifyEmail() {
  const [resendLoading, setResendLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { currentUser, sendEmailVerification, reloadUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleResend = async () => {
    if (!currentUser) return;
    try {
      setMessage(null);
      setResendLoading(true);
      await sendEmailVerification();
      setMessage({ type: 'success', text: 'Verification email sent. Please check your inbox and spam folder.' });
    } catch (error: any) {
      console.error('Resend verification error:', error);
      if (error?.code === 'auth/too-many-requests') {
        setMessage({ type: 'error', text: 'Too many requests. Please wait a few minutes before trying again.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to send verification email. Please try again.' });
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerified = async () => {
    try {
      setMessage(null);
      setCheckLoading(true);
      await reloadUser();
      await apiClient.getCurrentUser();
      navigate('/', { replace: true });
    } catch (error: any) {
      if (error?.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setMessage({ type: 'error', text: 'Email not verified yet. Click the link in the email we sent you, then click "I\'ve verified my email" again.' });
      } else {
        setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
      }
    } finally {
      setCheckLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { replace: true });
    }
  }, [currentUser, navigate]);

  if (!currentUser) {
    return null;
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 440 }}>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <MarkEmailReadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography component="h1" variant="h5" fontWeight="bold">
                Verify your email
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                We sent a verification link to <strong>{currentUser.email}</strong>. Click the link in that email to verify your account.
              </Typography>
            </Box>

            {message && (
              <Alert severity={message.type} sx={{ mb: 2 }}>
                {message.text}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerified}
                disabled={checkLoading}
                sx={{ py: 1.5 }}
              >
                {checkLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "I've verified my email"
                )}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResend}
                disabled={resendLoading}
                sx={{ py: 1.5 }}
              >
                {resendLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Resend verification email'
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => logout().then(() => navigate('/login'))}
                sx={{ cursor: 'pointer' }}
              >
                Sign out and use a different account
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
